//------------------------------------------------------------------------------------

import '../internal.scss';
import './Reports.scss';

import { useEffect, useState } from 'react';

import Buffer from '../../../components/Buffer/Buffer';
import Fallback from '../../../components/Fallback/Fallback';
import Footer from '../../../components/Footer/Footer';
import Header from '../../../components/Header/Header';
import Navigation from '../../../components/Navigation/Navigation';
import auth from '../../../scripts/auth';
import axios from 'axios';
import baseUrl from '../../../scripts/baseUrl';
import filterVendorsByDepartment, {
	isCateringVendor,
} from '../../../scripts/filterVendorsByDepartment';
import filterVendorsByStation from '../../../scripts/filterVendorsByStation';
import { saveAs } from 'file-saver';
import unauthorizedRedirection from '../../../scripts/unauthorizedRedirection';

//------------------------------------------------------------------------------------

const isActiveVendor = (vendor) => vendor.isDisabled != '1';

const emptyFilters = { station: '', designation: '', unit: '' };

//------------------------------------------------------------------------------------

const Reports = () => {
	unauthorizedRedirection();
	document.title = `Reports • Bhusawal Division Catering Vendor Id`;

	const [vendors, setVendors] = useState([]);
	const [notFound, setNotFound] = useState(true);
	const [buffer, setBuffer] = useState(true);

	const [filters, setFilters] = useState({ ...emptyFilters });
	const [appliedFilters, setAppliedFilters] = useState({ ...emptyFilters });

	const loggedInAdmin =
		localStorage.getItem('admin') != null && localStorage.getItem('admin') != ''
			? JSON.parse(localStorage.getItem('admin'))
			: null;
	const isSuperAdmin = loggedInAdmin && loggedInAdmin.superAdmin == 1;

	const [departmentView, setDepartmentView] = useState('All');

	const matchesDepartmentView = (vendor) => {
		if (!isSuperAdmin) return true;
		if (departmentView === 'Catering') return isCateringVendor(vendor);
		if (departmentView === 'Other') return !isCateringVendor(vendor);
		return true;
	};

	let accessToken =
		(localStorage.getItem('admin') == null ||
			localStorage.getItem('admin') == '') &&
		(localStorage.getItem('employee') != null ||
			localStorage.getItem('employee') != '')
			? JSON.parse(localStorage.getItem('employee')).accessToken
			: JSON.parse(localStorage.getItem('admin')).accessToken;

	const getVendors = async () => {
		try {
			const res = await axios.get(`${baseUrl}/reports/vendors`, {
				headers: {
					'access-token': accessToken,
				},
			});

			if (!auth(res.data.message)) return;

			let vendorsArray =
				(localStorage.getItem('admin') == null ||
					localStorage.getItem('admin') == '') &&
				(localStorage.getItem('employee') != null ||
					localStorage.getItem('employee') != '')
					? filterVendorsByStation(res.data)
					: filterVendorsByDepartment(res.data);

			if (res.data) {
				setVendors(vendorsArray);
				setNotFound(false);
			} else {
				setNotFound(true);
			}
			setBuffer(false);
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		getVendors();
	}, []);

	const handleFilterChange = (e) => {
		setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const runSearch = () => {
		setAppliedFilters({ ...filters });
	};

	const clearSearch = () => {
		setFilters({ ...emptyFilters });
		setAppliedFilters({ ...emptyFilters });
	};

	const enterClick = (e) => {
		if (e.key === 'Enter' || e.keyCode === 13) {
			runSearch();
		}
	};

	const hasSearched =
		appliedFilters.station.trim() !== '' ||
		appliedFilters.designation.trim() !== '' ||
		appliedFilters.unit.trim() !== '';

	const isWildcard = (value) => {
		const trimmed = value.trim().toLowerCase();
		return trimmed === '' || trimmed === 'all';
	};

	const matchesFilters = (vendor) => {
		const stationMatch =
			isWildcard(appliedFilters.station) ||
			(vendor.station || '')
				.toLowerCase()
				.includes(appliedFilters.station.trim().toLowerCase());

		const designationMatch =
			isWildcard(appliedFilters.designation) ||
			(vendor.desigination || '')
				.toLowerCase()
				.includes(appliedFilters.designation.trim().toLowerCase());

		const unitMatch =
			isWildcard(appliedFilters.unit) ||
			(vendor.unit || '')
				.toLowerCase()
				.includes(appliedFilters.unit.trim().toLowerCase());

		return (
			stationMatch &&
			designationMatch &&
			unitMatch &&
			isActiveVendor(vendor) &&
			matchesDepartmentView(vendor)
		);
	};

	const results = hasSearched
		? vendors
				.filter(matchesFilters)
				.sort((a, b) =>
					(a.unit || '').localeCompare(b.unit || '')
				)
		: [];

	const [downloading, setDownloading] = useState('');

	const buildExportRows = () =>
		results.map((vendor) => ({
			fullname: vendor.fullname || '',
			desigination: vendor.desigination || '',
			station: vendor.station || '',
			unit: vendor.unit || '',
		}));

	const downloadReport = async (format) => {
		setDownloading(format);
		try {
			const res = await axios.post(
				`${baseUrl}/reports/export/${format}`,
				{ rows: buildExportRows() },
				{
					headers: { 'access-token': accessToken },
					responseType: 'blob',
				}
			);
			saveAs(
				res.data,
				`vendors-report.${format === 'excel' ? 'xlsx' : 'pdf'}`
			);
		} catch (err) {
			console.log(err);
			alert(`Failed to download ${format === 'excel' ? 'Excel' : 'PDF'} report.`);
		}
		setDownloading('');
	};

	return (
		<>
			<Header />
			<div className='screen-container'>
				<Navigation />
				<div className='side-container'>
					{buffer && notFound ? <Buffer /> : <></>}

					{vendors.length === 0 && !buffer ? (
						<Fallback
							title='Not found'
							text='No data found in the database. Try refreshing the page.'
						/>
					) : (
						<></>
					)}

					{vendors.length !== 0 && !notFound && !buffer && (
						<>
							<div className='screen-title headline-large'>
								Reports
							</div>

							{isSuperAdmin && (
								<div className='department-tabs'>
									{['All', 'Catering', 'Other'].map(
										(view) => (
											<button
												key={view}
												type='button'
												className={
													departmentView === view
														? 'department-tab active'
														: 'department-tab'
												}
												onClick={() =>
													setDepartmentView(view)
												}
											>
												{view}
											</button>
										)
									)}
								</div>
							)}

							<div className='report-search-container'>
								<div className='report-search-field'>
									<div className='label label-medium'>
										Station
									</div>
									<input
										type='text'
										className='inputfield'
										name='station'
										placeholder='e.g. Bhusawal-Jn'
										value={filters.station}
										onChange={handleFilterChange}
										onKeyUp={enterClick}
									/>
								</div>

								<div className='report-search-field'>
									<div className='label label-medium'>
										Designation
									</div>
									<input
										type='text'
										className='inputfield'
										name='designation'
										placeholder='e.g. Stall keeper'
										value={filters.designation}
										onChange={handleFilterChange}
										onKeyUp={enterClick}
									/>
								</div>

								<div className='report-search-field'>
									<div className='label label-medium'>
										Unit
									</div>
									<input
										type='text'
										className='inputfield'
										name='unit'
										placeholder='e.g. firm name'
										value={filters.unit}
										onChange={handleFilterChange}
										onKeyUp={enterClick}
									/>
								</div>

								<div className='report-search-buttons'>
									<button
										type='button'
										className='filled-button'
										onClick={runSearch}
									>
										Search
									</button>
									<button
										type='button'
										className='text-button'
										onClick={clearSearch}
									>
										Clear
									</button>
								</div>
							</div>

							{!hasSearched && (
								<Fallback
									title='Search to see results'
									text='Enter a station, designation, or unit (or combine them) above and click Search.'
								/>
							)}

							{hasSearched && results.length === 0 && (
								<Fallback
									title='No vendors found'
									text='No vendors match this station / designation / unit combination.'
								/>
							)}

							{hasSearched && results.length !== 0 && (
								<>
									<div className='report-summary-container'>
										<div className='report-summary-card'>
											<div className='report-summary-value headline-large'>
												{results.length}
											</div>
											<div className='report-summary-label title-small'>
												Total vendors
											</div>
										</div>
									</div>

									<div className='report-export-buttons'>
										<button
											type='button'
											className='filled-tonal-button'
											disabled={downloading !== ''}
											onClick={() =>
												downloadReport('excel')
											}
										>
											{downloading === 'excel'
												? 'Preparing Excel…'
												: 'Download Excel'}
										</button>
										<button
											type='button'
											className='filled-tonal-button'
											disabled={downloading !== ''}
											onClick={() =>
												downloadReport('pdf')
											}
										>
											{downloading === 'pdf'
												? 'Preparing PDF…'
												: 'Download PDF'}
										</button>
									</div>

									<div className='table-container'>
										<table>
											<thead>
												<tr>
													<th className='left-box'>
														<div className='table-head'>
															<div className='table-title'>
																Full&nbsp;name
															</div>
														</div>
													</th>
													<th>
														<div className='table-head'>
															<div className='table-title'>
																Designation
															</div>
														</div>
													</th>
													<th>
														<div className='table-head'>
															<div className='table-title'>
																Station
															</div>
														</div>
													</th>
													<th className='right-box'>
														<div className='table-head'>
															<div className='table-title'>
																Firm&nbsp;name
															</div>
														</div>
													</th>
												</tr>
											</thead>
											<tbody>
												{results.map((vendor) => (
													<tr key={vendor.id}>
														<td className='left-box'>
															{vendor.fullname ||
																'Unknown'}
														</td>
														<td className='center'>
															{vendor.desigination ||
																'--'}
														</td>
														<td className='center'>
															{vendor.station ||
																'--'}
														</td>
														<td className='center right-box'>
															{vendor.unit ||
																'--'}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</>
							)}
						</>
					)}

					<Footer />
				</div>
			</div>
		</>
	);
};

export default Reports;

//------------------------------------------------------------------------------------
